import React, { useEffect } from 'react';
import { Tabs, Alert, Form, Input, Button, Row, Col } from 'antd';
import { HTMLCode, JavascriptCode } from 'components/ScriptInstructions';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from 'hooks/apollo';
import useGlobalContext from 'hooks/useGlobalContext';
import useDataSource from 'hooks/useDataSource';
import { Link } from 'react-router-dom';
import Page from 'components/Page';
import Table from 'components/Table';

const SEND_INSTRUCTIONS = gql`
  mutation SendScriptInstructions($id: ID!, $email: String!) {
    sendScriptInstructions(id: $id, email: $email)
  }
`;

const VERIFY_SCRIPT = gql`
  query VerifyScript($url: String!) {
    verifyScript(url: $url)
  }
`;

const extraFields = `
  website { 
    id 
    url
    language
    languageName
    additionalLanguages {
      id
      language
      languageName
      url
    }
    script { id key } 
    customer { id name } 
  }
`;

const VerifyWebsiteUrl = ({ website }) => {
  const [verifyScript, { data, loading, error }] = useLazyQuery(VERIFY_SCRIPT, { variables: { url: website.url } });
  const { modal } = useGlobalContext();

  useEffect(() => {
    if (!loading) {
      if (error) {
        modal.error({ content: error.message });
      } else if (data) {
        if (data.verifyScript) {
          modal.success({ content: 'Script installed successfully' });
        } else {
          modal.error({ content: 'Script could not be detected, maybe it\'s not loaded until cookes are accepted?' });
        }
      }
    }
  }, [error, data, loading]);

  return (
    <Button type="primary" onClick={verifyScript} loading={loading}>Verify</Button>
  );
};

const ScriptSettings = () => {
  const { dataSource, dataSourceId } = useDataSource({ extraFields });
  const [form] = Form.useForm();
  const { message } = useGlobalContext();
  const [sendInstructions, { loading }] = useMutation(SEND_INSTRUCTIONS);

  const website = dataSource?.website;

  const onSendInstructions = async (values) => {
    try {
      await sendInstructions({ variables: { id: website.id, email: values.email } });
      form.resetFields();
      message.success('Instructions sent successfully');
    } catch (e) {
      message.error(e.message);
    }
  };

  const opts = {
    id: website?.script.key,
    title: website?.customer.name,
  };

  const tabs = [{
    key: '1',
    label: 'HTML code',
    children: (
      <>
        <p>Make sure this code is on every page of your website. We recommend pasting it immediately before the closing &lt;/body&gt; tag, for example in a common footer.</p>
        <HTMLCode {...opts} />
      </>
    ),
  }, {
    key: '2',
    label: 'Javascript code',
    children: (
      <>
        <p>If you need more control of how and when the script is executed it might be more convenient with javascript. Execute this code once on each page view.</p>
        <JavascriptCode {...opts} />
      </>
    ),
  }, {
    key: '3',
    label: 'Tag manager',
    children: (
      <>
        <p>If you use a tag manager you can install our script by creating a custom HTML tag and paste the following code. Trigger the tag on every page view.</p>
        <HTMLCode {...opts} />
      </>
    ),
  }, {
    key: '4',
    label: 'Send instructions',
    children: (
      <>
        <p>You can send the instructions to someone who can install the script.</p>
        <Form form={form} onFinish={onSendInstructions} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
                <Input type="email" placeholder="Email" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>Send</Button>
              </Form.Item>
            </Col>
          </Row>

        </Form>

      </>
    ),
  }];

  return (
    <Page>
      <Alert
        type="warning"
        showIcon
        message={(
          <>
            The script may track visitors and use cookies depending on your privacy & cookie settings.
          </>
            )}
        action={<Link to={`/data-sources/${dataSourceId}/settings/privacy`}><Button>View settings</Button></Link>}
      />
      <Page.Content title="Install script">
        <Tabs defaultActiveKey="1" items={tabs} />
      </Page.Content>
      <Page.Content title="Check script installation">
        <Table
          columns={[{
            title: 'Website urls',
            dataIndex: 'url',
          }]}
          dataSource={website ? [website, ...website.additionalLanguages] : []}
          actions={(record) => <VerifyWebsiteUrl website={record} />}
          pagination={false}
        />
      </Page.Content>
    </Page>
  );
};

export default ScriptSettings;
